import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';

import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FlooriIconComponent } from '../floori-icon';
import { FlooriButtonComponent } from '../floori-button';
import { FlooriControl } from '../../utils';
import { ValidInputTypes } from './models';
import { DisableEnterDirective } from './disable-enter.directive';
import { takeUntil, tap, merge } from 'rxjs';

@Component({
    selector: 'floori-input',
    standalone: true,
    imports: [FormsModule, FlooriIconComponent, FlooriButtonComponent, DisableEnterDirective],
    templateUrl: './floori-input.component.html',
    styleUrls: ['./floori-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FlooriInputComponent,
        },
    ],
})
export class FlooriInputComponent extends FlooriControl implements ControlValueAccessor, OnInit {
    @Input() type: ValidInputTypes = 'text';
    @Input() clear: boolean = true;
    @Input() disableSubmit: boolean = false;
    @Input() updateOn: 'blur' | 'change' = 'change';
    @Input() maxValue: string | number | undefined;
    @Input() allowFloats: boolean = false;
    @Input() allowNegative: boolean = true;
    @Input() showStarInPlaceholder: boolean = false;
    @Output() readonly changed = new EventEmitter<string>();
    private _placeholder = '';
    private _value: unknown = '';
    private _passwordVisible = false;
    private _minValue: number | undefined;
    private _stepValue: string = '1';
    private readonly controlKeys = Object.freeze([
        'Backspace',
        'Delete',
        'Tab',
        'Escape',
        'Enter',
        'Home',
        'End',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
    ]);

    @Input() set value(val: unknown) {
        if (this._value !== null && val !== this.value) {
            this._value = val;
            this.onChange(this._value);
            this.changed.emit(this._value as string);
            this.cdr.detectChanges();
            return;
        }
        this._value = val;
        this.cdr.detectChanges();
    }

    get value(): string {
        return this._value as string;
    }

    @Input() set placeholder(val: string) {
        if (val !== this._placeholder) {
            this._placeholder = !val ? '' : val;
            this.cdr.markForCheck();
        }
    }

    get placeholder(): string {
        return this._placeholder;
    }

    get isPasswordVisible(): boolean {
        return this._passwordVisible;
    }

    get minValue(): number | undefined {
        this._minValue = this.allowNegative ? undefined : 0;
        return this._minValue;
    }

    get stepValue(): string {
        if (this.type === 'number' && this.allowFloats) {
            this._stepValue = '0.01';
        }
        return this._stepValue;
    }

    @HostBinding('attr.disabled')
    get hostDisabled(): string | null {
        return this.disabled ? 'disabled' : null;
    }

    @HostBinding('attr.required')
    get hostRequired(): string | null {
        return this.required ? 'required' : null;
    }

    @HostBinding('attr.readonly')
    get hostReadonly(): string | null {
        return this.readonly ? 'readonly' : null;
    }

    @HostBinding('class')
    override get classBindings(): string[] {
        const isInvalid =
            this.control &&
            (!this.control.pristine || this.control.touched) &&
            this.control.invalid;

        this._invalid = isInvalid || false;

        const baseClasses = super.classBindings;

        return ['floori-input', ...baseClasses].filter(c => !!c);
    }

    @HostBinding('class.textarea')
    get isTextArea(): boolean {
        return this.type === 'textarea';
    }

    get isPassword(): boolean {
        return this.type === 'password';
    }

    get resolvedInputType(): string {
        if (this.isPasswordVisible) {
            return 'text';
        }
        return this.type === 'number' ? 'text' : this.type;
    }

    writeValue(value: unknown): void {
        if (this.onChangeInit) {
            this.value = value;
        }
    }

    clearValue(): void {
        this.input?.nativeElement.focus();
        this.value = '';
    }

    changeVisibility(): void {
        this._passwordVisible = !this._passwordVisible;
    }

    override focusChanged(focused: boolean): void {
        super.focusChanged(focused);
        this.updateInvalidState();
    }

    protected override setupControl(): void {
        super.setupControl();
        this.updateInvalidState();

        if (this.updateOn !== 'change') {
            return;
        }

        const statusChanges$ = this.control?.statusChanges || [];
        const valueChanges$ = this.control?.valueChanges || [];

        merge(statusChanges$, valueChanges$)
            .pipe(
                takeUntil(this.destroyed),
                tap(() => {
                    this.updateInvalidState();
                }),
            )
            .subscribe();
    }

    handleKeydown(event: KeyboardEvent): void {
        if (this.type === 'number') {
            const isControlKey = this.controlKeys.includes(event.key);
            const isCtrlCombination =
                (event.ctrlKey || event.metaKey) &&
                ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase());

            if (isControlKey || isCtrlCombination) {
                return;
            }

            const allowedKeys = ['-', '.', ','];
            const isDigit = /\d/.test(event.key);
            if (isDigit || allowedKeys.includes(event.key)) {
                return;
            }
            event.preventDefault();
        }
    }

    handleBeforeInput(event: InputEvent): void {
        if (this.type !== 'number') {
            return;
        }

        if (event.inputType.startsWith('delete') || event.inputType.startsWith('history')) {
            return;
        }
        const inputEl = this.input?.nativeElement as
            | HTMLInputElement
            | HTMLTextAreaElement
            | undefined;

        if (!inputEl) {
            return;
        }
        const data = (event.data ?? '').replace(',', '.');
        const start = inputEl.selectionStart ?? 0;
        const end = inputEl.selectionEnd ?? 0;
        const currentValue = String(inputEl.value ?? '');
        const nextValue = currentValue.slice(0, start) + data + currentValue.slice(end);

        const pattern = new RegExp(
            `^${this.allowNegative ? '-?' : ''}\\d*${this.allowFloats ? '(\\.\\d*)?' : ''}$`,
        );

        if (!pattern.test(nextValue)) {
            event.preventDefault();
            return;
        }

        if ((event.data ?? '') === ',') {
            event.preventDefault();
            inputEl.setRangeText('.', start, end, 'end');
            this._value = inputEl.value;
            this.cdr.markForCheck();
        }
    }

    private updateInvalidState(): void {
        if (this.control) {
            this._invalid =
                ((!this.control.pristine || this.control.touched) && this.control.invalid) || false;
            this.cdr.markForCheck();
        }
    }
}
